import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Columns3 } from "lucide-react";

const KEYS = [["total_return_pct", "Return %"], ["max_drawdown_pct", "Max DD %"], ["sharpe", "Sharpe"], ["profit_factor", "Profit factor"], ["win_rate_pct", "Win %"], ["trades", "Trades"], ["fees_paid", "Fees"]];

export function StrategyCompare({ refreshKey }) {
  const runs = useQuery({ queryKey: ["bt-runs", refreshKey], queryFn: async () => (await api.get("/backtest/runs")).data });
  const [sel, setSel] = useState([]);
  const [ids, setIds] = useState("");
  const cmp = useQuery({ queryKey: ["bt-compare", ids], enabled: ids.split(",").length >= 2,
    queryFn: async () => (await api.get(`/backtest/compare?ids=${ids}`)).data });
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id].slice(-6));
  const items = runs.data?.items || [];
  return (
    <Panel title="Saved runs · strategy comparison" testId="strategy-compare" actions={<Columns3 className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        {!items.length && <div data-testid="bt-runs-empty" className="text-slate-500">No saved runs yet.</div>}
        <div className="space-y-1 max-h-48 overflow-y-auto">{items.map((r) => (
          <label key={r.id} className="flex items-center gap-2 text-slate-300" data-testid={`bt-saved-${r.id}`}>
            <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`bt-select-${r.id}`} />
            <span className="font-mono">{r.spec.strategy} {r.spec.symbol} {r.spec.interval} · fee {r.spec.fee_bps} · slip {r.spec.slippage_bps} · OOS {r.out_of_sample?.total_return_pct ?? "—"}%</span>
          </label>))}</div>
        <Button data-testid="bt-compare-btn" size="sm" variant="outline" disabled={sel.length < 2} onClick={() => setIds(sel.join(","))}>Compare {sel.length} runs</Button>
        {cmp.data && <CompareTable d={cmp.data} />}
      </div>
    </Panel>
  );
}

function CompareTable({ d }) {
  return (
    <div className="overflow-x-auto space-y-2" data-testid="bt-compare-table">
      {!d.same_dataset && <div className="text-amber-300" data-testid="bt-compare-dataset-warning">Runs use different datasets — compare with care.</div>}
      <table className="w-full">
        <thead><tr className="text-left text-slate-500"><th className="py-1 pr-3">Metric</th>{d.items.map((r) => <th key={r.id} className="pr-3 font-mono">{r.strategy} · {r.id.slice(-5)}{r.id === d.best_out_of_sample ? " ★" : ""}</th>)}</tr></thead>
        <tbody>
          <tr className="border-t border-[var(--border-subtle)] text-slate-400"><td className="py-1">Fee / slippage bps</td>{d.items.map((r) => <td key={r.id}>{r.fee_bps} / {r.slippage_bps}</td>)}</tr>
          {KEYS.map(([k, l]) => ["out_of_sample", "in_sample"].map((seg) => (
            <tr key={`${k}-${seg}`} className="border-t border-[var(--border-subtle)] text-slate-300">
              <td className="py-1">{l} <span className="text-slate-500">{seg === "in_sample" ? "IS" : "OOS"}</span></td>
              {d.items.map((r) => <td key={r.id} data-testid={`cmp-${seg}-${k}-${r.id}`}>{r[seg]?.[k] ?? "—"}</td>)}
            </tr>)))}
          <tr className="border-t border-[var(--border-subtle)] text-slate-300"><td className="py-1">OOS − IS return</td>{d.items.map((r) => <td key={r.id}>{r.oos_degradation_pct ?? "—"}</td>)}</tr>
        </tbody>
      </table>
      <div className="text-slate-500">{d.note} ★ = best out-of-sample return.</div>
    </div>
  );
}
